import type { CalendarEvent } from '@highlight/common'
import type { RecentActivity } from '../../types'
import type { EarlierTodayItem, UpcomingSection } from './use-upcoming-view-model'

export type UpcomingRow =
  | { kind: 'label'; key: string; label: string; isFirst: boolean }
  | { kind: 'note'; key: string; note: RecentActivity }
  /** Past event nothing was recorded for — renders compact, like a note row. */
  | { kind: 'past-event'; key: string; event: CalendarEvent }
  | { kind: 'event'; key: string; event: CalendarEvent }
  | { kind: 'clear-day'; key: string }

function earlierTodayItemId(item: EarlierTodayItem | undefined): string | undefined {
  if (!item) return undefined
  return item.type === 'note' ? item.note.id : item.event.id
}

export function toUpcomingRows(sections: Array<UpcomingSection>): Array<UpcomingRow> {
  const rows: Array<UpcomingRow> = []

  sections.forEach((section, sectionIndex) => {
    const sectionIdentity =
      section.kind === 'events'
        ? `${section.label}:${section.events[0]?.id ?? 'empty'}`
        : section.kind === 'earlier-today'
          ? `${section.label}:${earlierTodayItemId(section.items[0]) ?? 'empty'}`
          : section.label
    rows.push({
      kind: 'label',
      key: `label:${section.kind}:${sectionIdentity}`,
      label: section.label,
      isFirst: sectionIndex === 0,
    })

    switch (section.kind) {
      case 'earlier-today':
        rows.push(
          ...section.items.map(
            (item): UpcomingRow =>
              item.type === 'note'
                ? { kind: 'note', key: `note:${item.note.id}`, note: item.note }
                : { kind: 'past-event', key: `past-event:${item.event.id}`, event: item.event },
          ),
        )
        break
      case 'clear-day':
        rows.push({ kind: 'clear-day', key: `clear-day:${section.label}` })
        break
      case 'events':
        rows.push(...section.events.map((event) => ({ kind: 'event' as const, key: `event:${event.id}`, event })))
        break
    }
  })

  return rows
}
